import React, { useState, useRef } from "react";
import { Button, Slider, Select, Paper, Flex, FileButton, Box, Title } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import styles from "./style.module.css";

const ImageCompressorComponent: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [compressedImage, setCompressedImage] = useState<string | null>(null);
    const [compressionLevel, setCompressionLevel] = useState<number>(75);
    const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(null);
    const [outputFormat, setOutputFormat] = useState<string>("image/jpeg");
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const handleFileChange = (file: File | null) => {
        const reader = new FileReader();
        if (file) {
            setSelectedFile(file);
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    function getImageDimensions(file: File): Promise<number> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = function(event) {
                const img = new Image();
                img.onload = function() {
                    const maxSideSize = Math.max(img.width, img.height);
                    resolve(Math.round((maxSideSize) * (compressionLevel / 100)));
                };
                img.onerror = function() {
                    reject(new Error("Error loading image"));
                };
                img.src = event?.target?.result as string;
            };

            reader.onerror = function() {
                reject(new Error("Error reading file"));
            };

            reader.readAsDataURL(file);
        });
    }

    async function compressImage(): Promise<void> {
        if (!selectedFile) return;
        const maxWidthOrHeight: number = await getImageDimensions(selectedFile);

        const imageCompression = await import("browser-image-compression");
        const options = {
            maxSizeMB: 10,
            maxWidthOrHeight: maxWidthOrHeight,
            useWebWorker: true,
            alwaysKeepResolution: true,
            fileType: outputFormat
        };
        const file = await imageCompression.default(
            selectedFile as File, options);
        /**
        * Converting the file to a string
        */
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.readyState === FileReader.DONE) {
                const dataUrl = event.target.result as string;
                setCompressedImage(dataUrl);
            } 
        };
        reader.readAsDataURL(file);
    }

    const handleDownload = () => {
        if (!compressedImage) return;
        const link = document.createElement("a");
        link.href = compressedImage;
        link.download = `compressed_image.${outputFormat.split("/")[1]}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        resetPageToDefault();
    };

    const resetPageToDefault = (): void => {
        setSelectedFile(null);
        setCompressedImage(null);
        setCompressionLevel(75);
        setImagePreview(null);
        setOutputFormat("image/jpeg");
    };

    return (
        <>
            <Title order={1} mt="3em" mb=".5em" pl=".5em" className={styles.title}>Compress Images</Title>
            <Paper className={styles.paper} p="2em" radius="lg" mb="5em">
                <Flex align='center' justify='space-evenly'>
                    <Flex align='center' direction="column" gap="0.5em">
                        <Box
                            className={styles["img-box"]}
                            h="360"
                            w="560"
                            style={{ backgroundImage: `url(${imagePreview})` }}
                        >
                            {!imagePreview ? <FileButton
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={handleFileChange}
                            >
                                {(props) => <Button 
                                    size="lg" 
                                    aria-label="Upload Image" {...props}
                                >
                                    Upload image
                                </Button>
                                }
                            </FileButton> : <Button aria-label="Reset" onClick={() => setImagePreview(null)}>Reset</Button>}
                        </Box>
                    </Flex>
                    <Flex direction="column" justify="space-between">
                        <Flex w="300" direction="column">
                            <Box mb="1em">
                                <label htmlFor="compression-level">Compression Level</label>
                                <Slider
                                    id="compression-level"
                                    label={(value: number) => value}
                                    value={compressionLevel}
                                    onChange={setCompressionLevel}
                                    min={0}
                                    max={100}
                                    mb="1em"
                                    precision={1}
                                    marks={[
                                        { value: 0, label: "0%" },
                                        { value: 50, label: "50%" },
                                        { value: 100, label: "100%" },
                                    ]}
                                />
                            </Box>
                            <label htmlFor="output-format">Output Format</label>
                            <Select
                                mt="sm"
                                w={100}
                                value={outputFormat}
                                onChange={(value: string | null) => setOutputFormat(value || "image/jpeg")}
                                data={[
                                    { value: "image/jpeg", label: "JPEG" },
                                    { value: "image/png", label: "PNG" },
                                    { value: "image/webp", label: "WebP" },
                                ]}
                                mb="lg"
                            />
                        </Flex>
                        <Box pt="2em">
                            <Button.Group>
                                <Button 
                                    size="lg" 
                                    onClick={compressImage} mb="md"
                                    disabled={!selectedFile}
                                    className={styles["compress-image-button"]}
                                >
                                    Compress Image
                                </Button>
                                <Button 
                                    className={styles["download-btn"]}
                                    size="lg"
                                    onClick={handleDownload} 
                                    disabled={compressedImage ? false : true}
                                >
                                    <IconDownload style={{ width: "70%", height: "70%" }} stroke={1.5} />
                                </Button>
                            </Button.Group>
                        </Box>
                    </Flex>
                </Flex>
                <canvas ref={canvasRef} style={{ display: "none" }} />
            </Paper>
        </>
    );
};

export default ImageCompressorComponent;
